// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Purge confirmation dialog for I-9 documents
 *
 * Confirms purging a shredded document: it queues deletion from Optix, then the
 * backend moves it to the Purged bucket after Optix confirms success. Only shredded
 * documents can be purged (re-purging a purged document whose Optix delete failed
 * retries just the delete).
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@/components/ui'
import { Trash2 } from 'lucide-react'

interface PurgeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  docId: string | null
  isPurging: boolean
  onConfirm: () => void
}

export const PurgeDialog: FC<PurgeDialogProps> = ({
  open,
  onOpenChange,
  docId,
  isPurging,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-destructive" />
          Purge Document
        </DialogTitle>
        <DialogDescription>
          Purge <span className="font-mono">{docId}</span>? This queues permanent
          deletion from Optix. After Optix confirms deletion, the document moves to
          the Purged bucket. This cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={isPurging || !docId}>
          <Trash2 className="h-4 w-4 mr-1" />
          Purge
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
